import { useState, useMemo, useEffect, useRef} from 'react';
import { reconcileJobAssignments } from '@/lib/split/reconcileJobAssignments';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertTriangle, CheckCircle2, Scissors } from 'lucide-react';
import { deriveSplitOperationId } from '@/lib/tickets/optiPlaceAnalysisInput';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { edgeRefusalMessage } from '@/utils/assignment/smartAssignRefusal';
import { useQueryClient } from '@tanstack/react-query';
import { getJobStage, getStageName, validateStageOrdering, JOB_STAGES } from '@/lib/jobStageConstants';
import { cn } from '@/lib/utils';

interface Job {
  id: string;
  name: string;
  category: string;
  standardMinutes: number;
}

interface Technician {
  id: string;
  name: string;
  is_present: boolean;
}

interface ManualSplitModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
  ticketJobs: Job[];
  currentTechId: string | null;
  currentTechName: string;
  technicians: Technician[];
  shopId: string;
  timerStatus?: string;
  vehicleInfo?: {
    year?: number;
    make?: string;
    model?: string;
  };
}

interface JobAssignment {
  jobId: string;
  techId: string;
}

export const ManualSplitModal = ({
  isOpen,
  onClose,
  ticketId,
  ticketJobs,
  currentTechId,
  currentTechName,
  technicians,
  shopId,
  timerStatus,
  vehicleInfo,
}: ManualSplitModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize job assignments - all jobs start with current tech
  const [jobAssignments, setJobAssignments] = useState<JobAssignment[]>([]);
  
  // Core-100 R5: the caller passes `ticketJobs` as a NEW array on every render (TicketCard maps it inline), so keying
  // the reset on the array's identity silently put every job back on the current technician whenever the card
  // re-rendered while the modal was open (a realtime update, a refetch) — discarding the advisor's choices (found by the
  // R5 browser proof: the preview showed the split, then Confirm was disabled). Now the assignments are RESET only when
  // the modal opens; while it is open a choice is kept for every job still present and only a new job is defaulted.
  const jobSignature = ticketJobs.map((j) => j.id).join('|');
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const opening = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!isOpen || ticketJobs.length === 0) return;
    setJobAssignments((prev) => reconcileJobAssignments(prev, ticketJobs.map((j) => j.id), currentTechId, opening));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the job ids, not the array identity (see above)
  }, [isOpen, jobSignature, currentTechId]);

  // Check for timer guard
  const isTimerRunning = timerStatus === 'running';

  // Available technicians (only present ones)
  const availableTechs = useMemo(() => 
    technicians.filter(t => t.is_present),
    [technicians]
  );

  // Get technician name by ID
  const getTechName = (techId: string): string => {
    if (!techId) return 'Unassigned';
    const tech = technicians.find(t => t.id === techId);
    return tech?.name || 'Unknown';
  };

  // Calculate resulting ticket chunks
  const resultingChunks = useMemo(() => {
    const techGroups: Record<string, { 
      techId: string; 
      techName: string; 
      jobs: Job[];
      totalMinutes: number;
    }> = {};
    
    jobAssignments.forEach(assignment => {
      const job = ticketJobs.find(j => j.id === assignment.jobId);
      if (!job) return;
      
      if (!techGroups[assignment.techId]) {
        techGroups[assignment.techId] = {
          techId: assignment.techId,
          techName: getTechName(assignment.techId),
          jobs: [],
          totalMinutes: 0,
        };
      }
      
      techGroups[assignment.techId].jobs.push(job);
      techGroups[assignment.techId].totalMinutes += job.standardMinutes;
    });
    
    return Object.values(techGroups);
  }, [jobAssignments, ticketJobs, technicians]);

  // Validate stage ordering
  const stageValidation = useMemo(() => {
    const chunksForValidation = resultingChunks.map(chunk => ({
      techId: chunk.techId,
      techName: chunk.techName,
      jobs: chunk.jobs.map(j => ({ id: j.id, name: j.name, category: j.category })),
    }));
    return validateStageOrdering(chunksForValidation);
  }, [resultingChunks]);

  // Check if all jobs go to same tech (no actual split)
  const isNoSplitNeeded = resultingChunks.length <= 1;

  // Handle job assignment change
  const handleAssignmentChange = (jobId: string, techId: string) => {
    setJobAssignments(prev => 
      prev.map(a => a.jobId === jobId ? { ...a, techId } : a)
    );
  };

  // Handle confirm split
  const handleConfirmSplit = async () => {
    if (isTimerRunning) {
      toast({
        title: 'Timer Running',
        description: 'Please pause or stop this ticket\'s timer before splitting it.',
        variant: 'destructive',
      });
      return;
    }

    if (isNoSplitNeeded) {
      toast({
        title: 'No Split Needed',
        description: 'All jobs are assigned to the same technician.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // 8G.2D.2C §10 — one CONFIRMED proposal owns one operation identity.
      //
      // Keyed by the proposal itself, so a retry of the same confirmed split
      // reuses the key (and the database answers ALREADY_APPLIED with the
      // family it already created), while genuinely changing the job
      // assignments is a different structural decision and gets a new one. A
      // bare rerender changes neither.
      // 8G.2D.2D.2 §6 — derived, so it survives a page refresh. A stored key
      // meant a committed-but-unacknowledged split could not be recognised on
      // retry once the tab reloaded.
      const operationId = await deriveSplitOperationId(
        ticketId,
        [...jobAssignments]
          .sort((a, b) => a.jobId.localeCompare(b.jobId))
          .map((a) => ({ job_ids: [a.jobId], tech_id: a.techId })),
      );

      // `userId` is deliberately NOT sent. The structural actor is auth.uid()
      // inside the authority; a body field could name anyone, and measurably
      // did — a manager's split once recorded a technician as having acted.
      const { data, error } = await supabase.functions.invoke('execute-manual-split', {
        body: {
          originalTicketId: ticketId,
          jobAssignments,
          shopId,
          operationId,
          vehicleInfo,
        },
      });

      // Core-100 R4 (CORE-AT): a refusal the operator can act on (e.g. a part would miss the Due Out) is shown
      // as the server stated it; anything unsafe or unexpected keeps the fixed fallback.
      if (error) throw new Error(await edgeRefusalMessage(error, 'Failed to split ticket'));

      if (data?.error) {
        throw new Error(data.error);
      }

      // Invalidate queries to refresh the board
      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      await queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });

      toast({
        title: 'Ticket Split Successfully',
        description: `Created ${data.newTickets?.length || resultingChunks.length} new tickets`,
      });

      onClose();
    } catch (error: any) {
      console.error('[MANUAL-SPLIT] Error:', error);
      toast({
        title: 'Split Failed',
        description: error.message || 'Failed to split ticket',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get stage color
  const getStageColor = (category: string): string => {
    const stage = getJobStage(category);
    switch (stage) {
      case JOB_STAGES.INSPECTION:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case JOB_STAGES.MECHANICAL:
        return 'bg-slate-100 text-slate-800 dark:bg-slate-800/50 dark:text-slate-300';
      case JOB_STAGES.ALIGNMENT:
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case JOB_STAGES.ADAS:
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
      case JOB_STAGES.FINAL:
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="h-5 w-5" />
            Split Ticket
          </DialogTitle>
          <DialogDescription>
            Assign each job to a technician. Jobs will be grouped into separate tickets.
          </DialogDescription>
        </DialogHeader>

        {/* Timer Guard Warning */}
        {isTimerRunning && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Please pause or stop this ticket's timer before splitting it.
            </AlertDescription>
          </Alert>
        )}

        {/* Job Assignment Table */}
        <div className="space-y-4">
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left text-xs font-medium px-4 py-2">Job</th>
                  <th className="text-left text-xs font-medium px-4 py-2">Stage</th>
                  <th className="text-left text-xs font-medium px-4 py-2">Time</th>
                  <th className="text-left text-xs font-medium px-4 py-2">Assign To</th>
                </tr>
              </thead>
              <tbody>
                {ticketJobs.map((job, idx) => {
                  const stage = getJobStage(job.category);
                  const currentAssignment = jobAssignments.find(a => a.jobId === job.id)?.techId || '';
                  
                  return (
                    <tr 
                      key={job.id} 
                      className={cn(
                        "border-t",
                        idx % 2 === 0 ? "bg-background" : "bg-muted/20"
                      )}
                    >
                      <td className="px-4 py-2">
                        <div className="font-medium text-sm">{job.name}</div>
                      </td>
                      <td className="px-4 py-2">
                        <Badge variant="outline" className={cn("text-xs", getStageColor(job.category))}>
                          {getStageName(stage)}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-sm text-muted-foreground">
                        {job.standardMinutes} min
                      </td>
                      <td className="px-4 py-2">
                        <Select
                          value={currentAssignment}
                          onValueChange={(value) => handleAssignmentChange(job.id, value)}
                          disabled={isTimerRunning || isSubmitting}
                        >
                          <SelectTrigger className="w-40 h-8 text-sm">
                            <SelectValue placeholder="Select tech" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableTechs.map(tech => (
                              <SelectItem key={tech.id} value={tech.id}>
                                {tech.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Preview Section */}
          <div className="border rounded-lg p-4 bg-muted/20">
            <Label className="text-sm font-semibold">Preview: Resulting Tickets</Label>
            
            {isNoSplitNeeded ? (
              <div className="mt-2 text-sm text-muted-foreground">
                All jobs assigned to same technician - no split needed.
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                {stageValidation.orderedChunks.map((chunk, idx) => (
                  <div 
                    key={chunk.techId} 
                    className="flex items-center justify-between bg-background rounded-md px-3 py-2 border"
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        Part {idx + 1}
                      </Badge>
                      <span className="font-medium text-sm">{chunk.techName}</span>
                      <span className="text-xs text-muted-foreground">
                        ({chunk.jobs.length} job{chunk.jobs.length > 1 ? 's' : ''})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={cn("text-xs", getStageColor(chunk.jobs[0]?.category || ''))}>
                        {chunk.stageName}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {resultingChunks.find(c => c.techId === chunk.techId)?.totalMinutes || 0} min
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Stage Validation */}
            {!isNoSplitNeeded && (
              <div className="mt-3">
                {stageValidation.isValid ? (
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <CheckCircle2 className="h-4 w-4" />
                    Stage ordering valid - work will be scheduled in correct sequence
                  </div>
                ) : (
                  <div className="space-y-1">
                    {stageValidation.warnings.map((warning, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        {warning}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmSplit}
            disabled={isTimerRunning || isNoSplitNeeded || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Splitting...
              </>
            ) : (
              'Confirm Split'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
